import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TablePagination,
  Chip,
  Skeleton,
  Snackbar,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Flight as AirIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { generateFactorVersionPDF, generateAirFactorsPDF } from '../services/factorVersionPdf';

// ============================================================================
// Types
// ============================================================================

interface ShipmentSearchResult {
  dbid: number;
  reference: string;
  customer_reference: string;
  total_co2: number;
  total_co2e: number;
  total_co2wtw: number;
  calculated_at: string;
  company_id: number;
  company_name: string;
  api_key: string;
  factor_version: string | null;
}

interface FactorVersionInfo {
  version: string;
  apiVersion: string;
  methodology: string;
  description: string;
  createdAt: string;
}

// ============================================================================
// Factor Version Dialog
// ============================================================================

interface FactorDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  pdfServiceUrl: string;
  setToast: (toast: { message: string; severity: 'info' | 'success' | 'error' }) => void;
}

const FactorDownloadDialog: React.FC<FactorDownloadDialogProps> = ({ open, onClose, pdfServiceUrl, setToast }) => {
  const [versions, setVersions] = useState<FactorVersionInfo[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    axios.get(`${pdfServiceUrl}/api/emission-factors/versions`)
      .then((res) => {
        if (res.data.success && res.data.versions.length > 0) {
          setVersions(res.data.versions);
          setSelectedVersion(res.data.versions.at(-1).version);
        }
      })
      .catch(() => setError('Failed to load versions'))
      .finally(() => setLoading(false));
  }, [open, pdfServiceUrl]);

  const selected = versions.find(v => v.version === selectedVersion);

  const handleDownload = async (type: 'factors' | 'air-en' | 'air-iso') => {
    if (!selectedVersion) return;
    setGenerating(type);
    setError(null);

    try {
      if (type === 'factors') {
        const res = await axios.get(`${pdfServiceUrl}/api/emission-factors/versions/${selectedVersion}`);
        if (!res.data.success) throw new Error('Failed to fetch');
        const doc = generateFactorVersionPDF(res.data.data, globalThis.location.origin);
        doc.save(`emission-factors-v${selectedVersion}.pdf`);
        setToast({ message: 'Emission factors PDF downloaded', severity: 'success' });
      } else {
        const airType = type === 'air-en' ? 'en' : 'iso';
        const res = await axios.get(`${pdfServiceUrl}/api/emission-factors/versions/${selectedVersion}/air/${airType}`);
        if (!res.data.success) throw new Error(`No ${airType.toUpperCase()} air data for this version`);
        const doc = generateAirFactorsPDF(res.data.data, airType.toUpperCase() as 'EN' | 'ISO', res.data.version);
        doc.save(`air-emission-factors-${airType}-v${selectedVersion}.pdf`);
        setToast({ message: `Air ${airType.toUpperCase()} PDF downloaded`, severity: 'success' });
      }
    } catch (err: any) {
      setError(err.message || 'PDF generation failed');
    } finally {
      setGenerating(null);
    }
  };

  const isEN = selected?.methodology === 'EN';
  const airCard = isEN
    ? { id: 'air-en' as const, title: 'Air Factors (EN)', description: '14,000 distance-based generic belly and freighter entries' }
    : { id: 'air-iso' as const, title: 'Air Factors (ISO)', description: 'Belly and freighter factors by region (EU/NA) and distance' };

  const downloadCards = [
    {
      id: 'factors' as const,
      icon: <DocIcon sx={{ fontSize: 32 }} />,
      title: 'Emission Factors',
      description: 'Complete reference of all truck, rail, sea, IWW, and ferry emission factors',
    },
    {
      id: airCard.id,
      icon: <AirIcon sx={{ fontSize: 32 }} />,
      title: airCard.title,
      description: airCard.description,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
        px: 3, py: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.02em' }}>
            Download Emission Factors
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            Generate PDF reference documents
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {generating && <LinearProgress color="success" />}

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Version selector */}
        <FormControl fullWidth size="small">
          <InputLabel>Factor Version</InputLabel>
          <Select
            value={selectedVersion}
            label="Factor Version"
            onChange={(e) => setSelectedVersion(e.target.value)}
            disabled={loading}
          >
            {versions.map((v) => (
              <MenuItem key={v.version} value={v.version}>
                v{v.version} - {v.methodology}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selected.description}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Download cards */}
        {!loading && versions.length > 0 && (
          <Stack spacing={1.5} sx={{ mt: 2.5 }}>
            {downloadCards.map((card) => (
              <Paper
                key={card.id}
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: generating ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  borderColor: 'divider',
                  '&:hover': generating ? {} : {
                    borderColor: '#2e7d32',
                    boxShadow: '0 0 0 1px rgba(46,125,50,0.12)',
                    bgcolor: 'rgba(46,125,50,0.02)',
                  },
                }}
                onClick={() => !generating && handleDownload(card.id)}
              >
                <Box sx={{
                  width: 48, height: 48,
                  borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'rgba(46,125,50,0.08)',
                  color: '#2e7d32',
                  flexShrink: 0,
                }}>
                  {generating === card.id ? <CircularProgress size={22} color="success" /> : card.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {card.description}
                  </Typography>
                </Box>
                <PdfIcon sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }} />
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Shipments Page
// ============================================================================

const ShipmentsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchBy, setSearchBy] = useState<'dbid' | 'reference' | 'customer_reference'>('reference');
  const [searchQuery, setSearchQuery] = useState('');
  // Customer Reference is the only mode that can match partially; dbid and
  // Shipment Reference are always exact.
  const [matchMode, setMatchMode] = useState<'exact' | 'contains'>('exact');
  const [results, setResults] = useState<ShipmentSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [factorDialogOpen, setFactorDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'info' | 'success' | 'error' } | null>(null);

  const pdfServiceUrl = process.env.REACT_APP_PDF_SERVICE_URL || 'http://localhost:8080';
  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin;

  // Handle ?airPdf=en|iso&version=X.Y.Z — auto-generate and download air PDF
  const triggerAirPdfDownload = useCallback(async (type: 'en' | 'iso', version: string) => {
    setToast({ message: `Generating Air ${type.toUpperCase()} PDF for v${version}...`, severity: 'info' });
    try {
      const response = await axios.get(`${pdfServiceUrl}/api/emission-factors/versions/${version}/air/${type}`);
      if (!response.data.success) {
        setToast({ message: `No ${type.toUpperCase()} air factors available for v${version}`, severity: 'error' });
        return;
      }
      const doc = generateAirFactorsPDF(response.data.data, type.toUpperCase() as 'EN' | 'ISO', response.data.version);
      doc.save(`air-emission-factors-${type}-v${version}.pdf`);
      setToast({ message: `Air ${type.toUpperCase()} PDF downloaded successfully`, severity: 'success' });
    } catch (err) {
      console.error('Air PDF auto-download failed:', err);
      setToast({ message: `Failed to generate Air ${type.toUpperCase()} PDF`, severity: 'error' });
    }
  }, [pdfServiceUrl]);

  useEffect(() => {
    const airPdf = searchParams.get('airPdf');
    const version = searchParams.get('version');
    if (airPdf && version && (airPdf === 'en' || airPdf === 'iso')) {
      triggerAirPdfDownload(airPdf, version);
      searchParams.delete('airPdf');
      searchParams.delete('version');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, triggerAirPdfDownload]);

  const fetchResults = async (query: string, searchLink: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/shipments/search', {
        params: { query, searchBy: searchLink, matchMode },
      });
      if (response.data.success) {
        setResults(response.data.results);
        setTotalCount(response.data.results.length);
        if (response.data.results.length === 0) setError('No shipments found');
      } else {
        setError('Search failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to search shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) { setError('Please enter a search query'); return; }
    setPage(0);
    fetchResults(searchQuery, searchBy);
  };

  const handleDownloadPDF = async (dbid: number, reference: string, apiKey: string) => {
    try {
      globalThis.open(`${pdfServiceUrl}/xml/shipment/${dbid}/pdf?apikey=${apiKey}`, '_blank');
    } catch (err: any) {
      alert('Failed to download PDF: ' + (err.message || 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  return (
    <Box>
      <PageHeader
        description="Browse, filter, and manage all shipments with their calculated emissions."
        actions={
          <>
            {isMasterAdmin && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PdfIcon />}
                onClick={() => setFactorDialogOpen(true)}
                size="large"
              >
                Download Factors
              </Button>
            )}
          </>
        }
      />

      {/* Search Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <FormControl sx={{ minWidth: 200, marginBottom: 0 }}>
            <InputLabel>Search By</InputLabel>
            <Select
              value={searchBy}
              label="Search By"
              onChange={(e) => setSearchBy(e.target.value as any)}
            >
              <MenuItem value="dbid">Database ID (dbid)</MenuItem>
              <MenuItem value="reference">Shipment Reference</MenuItem>
              <MenuItem value="customer_reference">Customer Reference</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={(() => {
              if (searchBy === 'dbid') return 'Enter Database ID';
              if (searchBy === 'reference') return 'Enter Reference';
              return 'Enter Customer Reference';
            })()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1, marginBottom: 0 }}
            type={searchBy === 'dbid' ? 'number' : 'text'}
          />

          {searchBy === 'customer_reference' && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={matchMode}
              onChange={(_event, mode) => mode && setMatchMode(mode)}
              aria-label="Match mode"
              sx={{ height: 50 }}
            >
              <Tooltip title="Match the whole customer reference">
                <ToggleButton value="exact" aria-label="Exact match">Exact</ToggleButton>
              </Tooltip>
              <Tooltip title="Match anywhere inside the customer reference">
                <ToggleButton value="contains" aria-label="Partial match">Partial</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
            sx={{ height: 50, width: 150 }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {/* Error / Info */}
      {error && (
        <Alert severity={results.length === 0 || error === 'Please enter a search query' ? 'info' : 'error'} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Table */}
      {(loading || results.length > 0) && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Database ID</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Customer Reference</TableCell>
                  <TableCell>Licensee</TableCell>
                  <TableCell align="right">TOT (kg)</TableCell>
                  <TableCell align="right">OPS (kg)</TableCell>
                  <TableCell align="right">ENE (kg)</TableCell>
                  <TableCell>Factor Version</TableCell>
                  <TableCell>Calculated At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: rowsPerPage }, (_, i) => `skeleton-${i}`).map((rowKey) => (
                      <TableRow key={rowKey}>
                        <TableCell><Skeleton variant="text" width={50} /></TableCell>
                        <TableCell sx={{ maxWidth: 200 }}><Skeleton variant="text" width="90%" /></TableCell>
                        <TableCell sx={{ maxWidth: 200 }}><Skeleton variant="text" width="80%" /></TableCell>
                        <TableCell><Skeleton variant="text" width={120} /></TableCell>
                        <TableCell align="right"><Skeleton variant="text" width={60} sx={{ ml: 'auto' }} /></TableCell>
                        <TableCell align="right"><Skeleton variant="text" width={60} sx={{ ml: 'auto' }} /></TableCell>
                        <TableCell align="right"><Skeleton variant="text" width={60} sx={{ ml: 'auto' }} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                        <TableCell><Skeleton variant="text" width={140} /></TableCell>
                        <TableCell align="center"><Skeleton variant="circular" width={32} height={32} sx={{ mx: 'auto' }} /></TableCell>
                      </TableRow>
                    ))
                  : results
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((result) => (
                        <TableRow key={result.dbid} hover>
                          <TableCell>{result.dbid}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            {result.reference && result.reference.length > 50 ? (
                              <Tooltip title={result.reference} arrow>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{result.reference}</Typography>
                              </Tooltip>
                            ) : result.reference}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            {result.customer_reference && result.customer_reference.length > 30 ? (
                              <Tooltip title={result.customer_reference} arrow>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{result.customer_reference}</Typography>
                              </Tooltip>
                            ) : (result.customer_reference || '-')}
                          </TableCell>
                          <TableCell>{result.company_name || '-'}</TableCell>
                          <TableCell align="right">{result.total_co2wtw.toFixed(2)}</TableCell>
                          <TableCell align="right">{result.total_co2e.toFixed(2)}</TableCell>
                          <TableCell align="right">{result.total_co2.toFixed(2)}</TableCell>
                          <TableCell>
                            {result.factor_version
                              ? <Chip label={result.factor_version} size="small" color="info" variant="outlined" />
                              : <Typography variant="body2" color="text.disabled">—</Typography>}
                          </TableCell>
                          <TableCell>{formatDate(result.calculated_at)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download PDF">
                              <IconButton color="primary" onClick={() => handleDownloadPDF(result.dbid, result.reference, result.api_key)}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number.parseInt(e.target.value, 10)); setPage(0); }}
            />
          )}
        </Paper>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && !error && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No results yet</Typography>
          <Typography variant="body2" color="text.secondary">Enter a search query and click Search to find shipments</Typography>
        </Paper>
      )}

      {/* Factor Download Dialog */}
      <FactorDownloadDialog
        open={factorDialogOpen}
        onClose={() => setFactorDialogOpen(false)}
        pdfServiceUrl={pdfServiceUrl}
        setToast={setToast}
      />

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'info' ? null : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShipmentsPage;
